<template>
  <div>
    <template v-if="serviceTypeIsImagery">
      <div class="form-group row">
        <label for="imageryAssessmentBeforeDate" class="col-lg-4 label-text">
          Imagery Comparison Before Date
        </label>
        <div class="col-lg-8">
          <datepicker
            id="beforeDate"
            :class="'flex-grow-1'"
            format="MM/dd/yyyy"
            input-class="form-control bg-white"
            :clear-button="true"
            :use-utc="false"
            :disabled-dates="disabledBeforeDateConfig.disabledDates"
            v-model="inspectionService.imageryAssessmentBeforeDate"
            :bootstrap-styling="true"
            name="beforeDate"
            @opened="resetDisabledBeforeDates"
            @selected="updateDisabledAfterDates"
            :key="counterToReCreateDatepicker"
          >
            <div slot="afterDateInput" class="input-group-append">
              <span class="input-group-text">
                <font-awesome-icon icon="calendar-alt" />
              </span>
            </div>
          </datepicker>
        </div>
      </div>
      <div class="form-group row">
        <label for="imageryAssessmentBeforeSource" class="col-lg-4 label-text">
          Imagery Comparison Before Source
        </label>
        <div class="loading col-lg-5" v-if="sourcesLoading">
          Loading...
        </div>
        <div class="col-lg-8">
          <select v-if="!sourcesLoading"
            data-vv-as="Imagery Comparison Before Source"
            class="form-control"
            name="imageryAssessmentBeforeSource"
            v-model="inspectionService.beforeSource"
            ref="beforeSource"
          >
            <option value="" disabled>Select Imagery Comparison Before Source</option>
            <option v-for="option in sourceOptions"
              :key="option.id"
              :value="option.name"
            >
              {{ option.name }}
            </option>
          </select>
          <div class="invalid-feedback">{{ errors.first('imageryAssessmentBeforeSource') }}</div>
        </div>
      </div>
      <div class="form-group row" v-if="inspectionService.beforeSource === 'Other'">
        <label for="imageryAssessmentBeforeSourceNotes" class="col-lg-4 label-text">
          <span class="text-danger">*</span>Imagery Comparison Before Source Notes
        </label>
        <div class="col-lg-8">
          <textarea data-vv-as="Imagery Comparison Before Source Notes"
            type="textarea"
            class="form-control"
            name="imageryAssessmentBeforeSourceNotes"
            v-model="inspectionService.beforeSourceNotes"
            v-validate="'required_if:beforeSource,Other|max:100'"
            placeholder="Enter Imagery Comparison Before Source Notes"
            rows="2"
            maxlength="100"
          />
            <small v-if="!errors.first('imageryAssessmentBeforeSourceNotes')"
              class="form-text text-muted">
              Max 100 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('imageryAssessmentBeforeSourceNotes') }}
            </div>
        </div>
      </div>
      <div class="form-group row">
        <label for="imageryAssessmentAfterDate" class="col-lg-4 label-text">
          Imagery Comparison After Date
        </label>
        <div class="col-lg-8">
          <datepicker
            id="afterDate"
            :class="'flex-grow-1'"
            format="MM/dd/yyyy"
            input-class="form-control bg-white"
            :clear-button="true"
            :use-utc="false"
            :disabled-dates="disabledAfterDateConfig.disabledDates"
            v-model="inspectionService.imageryAssessmentAfterDate"
            name="afterDate"
            :bootstrap-styling="true"
            @opened="updateDisabledAfterDates"
            @selected="updateDisabledBeforeDates"
            :key="counterToReCreateDatepicker"
          >
            <div slot="afterDateInput" class="input-group-append">
              <span class="input-group-text">
                <font-awesome-icon icon="calendar-alt" />
              </span>
            </div>
          </datepicker>
          <div class="invalid-feedback">{{ errors.first('afterDate') }}
          </div>
        </div>
      </div>
      <div class="form-group row">
        <label for="imageryAssessmentAfterSource" class="col-lg-4 label-text">
          Imagery Comparison After Source
        </label>
        <div class="loading col-lg-5" v-if="sourcesLoading">
          Loading...
        </div>
        <div class="col-lg-8">
          <select v-if="!sourcesLoading"
            data-vv-as="Imagery Comparison After Source"
            class="form-control"
            name="imageryAssessmentAfterSource"
            v-model="inspectionService.afterSource"
            ref="afterSource"
          >
            <option value="" disabled>Select Imagery Comparison After Source</option>
            <option v-for="option in sourceOptions"
              :key="option.id"
              :value="option.name"
            >
              {{ option.name }}
            </option>
          </select>
          <div class="invalid-feedback">{{ errors.first('imageryAssessmentAfterSource') }}</div>
        </div>
      </div>
      <div class="form-group row" v-if="inspectionService.afterSource === 'Other'">
        <label for="imageryAssessmentAfterSourceNotes" class="col-lg-4 label-text">
          <span class="text-danger">*</span>Imagery Comparison After Source Notes
        </label>
        <div class="col-lg-8">
          <textarea data-vv-as="Imagery Comparison After Source Notes"
            type="textarea"
            class="form-control"
            name="imageryAssessmentAfterSourceNotes"
            v-model="inspectionService.afterSourceNotes"
            v-validate="'required_if:afterSource,Other|max:100'"
            placeholder="Enter Imagery Comparison After Source Notes"
            rows="2"
            maxlength="100"
          />
            <small v-if="!errors.first('imageryAssessmentAfterSourceNotes')"
              class="form-text text-muted">
              Max 100 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('imageryAssessmentAfterSourceNotes') }}
            </div>
        </div>
      </div>
    </template>
    <div class="form-group row">
      <label for="landownerPropertyPlans" class="col-lg-4 label-text">
        Landowner Plans for the Property
      </label>
      <div class="col-lg-8">
        <textarea data-vv-as="Landowner Property Plans"
          type="textarea"
          class="form-control"
          name="landownerPropertyPlans"
          v-model="inspectionService.landownerPropertyPlans"
          placeholder="Enter Landowner Plans for the Property"
          rows="2"
          maxlength="250"
        />
          <small v-if="!errors.first('landownerPropertyPlans')" class="form-text text-muted">
            Max 250 characters
          </small>
          <div class="invalid-feedback">{{ errors.first('landownerPropertyPlans') }}</div>
      </div>
    </div>
    <div class="form-group row">
      <label for="questionsForVof" class="col-lg-4 label-text">
        Questions for VOF
      </label>
      <div class="col-lg-8">
          <textarea data-vv-as="Questions for VOF"
            type="textarea"
            class="form-control"
            name="questionsForVof"
            v-model="inspectionService.questionsForVOF"
            placeholder="Enter Questions for VOF"
            rows="2"
            maxlength="250"
          />
            <small v-if="!errors.first('questionsForVof')" class="form-text text-muted">
              Max 250 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('questionsForVof') }}</div>
      </div>
    </div>
    <ObservationsForm>
    </ObservationsForm>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import VeeValidate from 'vee-validate';
import Datepicker from 'vuejs-datepicker';
import { LookupModules } from '@/store/moduleConfig';
import ObservationsForm from './ObservationsForm.vue';

const dict = {
  custom: {
    imageryAssessmentBeforeSourceNotes: {
      required_if: 'The Imagery Comparison Before Source Notes field is required when the Imagery Comparison Before Source field is Other.',
    },
    imageryAssessmentAfterSourceNotes: {
      required_if: 'The Imagery Comparison After Source Notes field is required when the Imagery Comparison After Source field is Other.',
    },
  },
};

VeeValidate.Validator.localize('en', dict);

function initialState() {
  return {
    disabledBeforeDateConfig: {
      disabledDates: {
        from: new Date(),
        to: null,
        includeDisabled: false,
      },
    },
    disabledAfterDateConfig: {
      disabledDates: {
        from: new Date(),
        to: null,
        includeDisabled: false,
      },
    },
    serverError: null,
    counterToReCreateDatepicker: 0,
  };
}
export default {
  name: 'InspectionServiceForm',
  components: {
    Datepicker,
    ObservationsForm,
  },
  props: {
    inspectionService: {
      type: Object,
    },
    serviceTypeIsImagery: {
      type: Boolean,
    },
  },
  // inject the parent validator
  inject: ['$validator'],
  data() {
    return initialState();
  },
  mounted() {
    this.$store.dispatch(`${LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.namespace}/fetchOptions`,
      LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.url);
  },
  computed: {
    ...mapState(LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.namespace, {
      sourcesLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.SERVICE_IMAGERY_ASSESSMENT_SOURCES.namespace, {
      sourceOptions: 'getActiveOptions',
    }),
  },
  methods: {
    /* These methods are to change the disabled dates on the datepickers based on the selections
    of the Before and After Dates. The counter is a way to force re-creating the datepicker
    so the disabledDates can be updated. See https://stackoverflow.com/a/54024973 */
    updateDisabledAfterDates(dateSelected) {
      this.counterToReCreateDatepicker += 1;
      this.disabledAfterDateConfig.disabledDates.to = dateSelected;
    },
    updateDisabledBeforeDates(dateSelected) {
      this.counterToReCreateDatepicker += 1;
      this.disabledBeforeDateConfig.disabledDates.from = dateSelected;
    },
    resetDisabledBeforeDates() {
      this.disabledBeforeDateConfig.disabledDates.from = new Date();
    },
  },
};
</script>
